import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getConsultantOnlineScheduleApi,
  saveConsultantOnlineScheduleApi,
} from "../../services/consultantManagement";
import { toastMessage } from "../../utils/toastMessage";

const toAmPm = (value) => {
  const [hours, minutes] = String(value).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "";
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

const buildTimeOptions = () => {
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const value = `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    options.push({ value, label: toAmPm(value) });
  }
  return options;
};

const TIME_OPTIONS = buildTimeOptions();

const todayDateKey = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
  const year = ist.getFullYear();
  const month = String(ist.getMonth() + 1).padStart(2, "0");
  const day = String(ist.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const currentIstMinutes = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
  return ist.getHours() * 60 + ist.getMinutes();
};

const parseMinutes = (value) => {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const canEditClockTime = (rule, hhmm, nowTick) => {
  if (!hhmm) return true;
  const today = todayDateKey();
  if (rule?.type === "range") {
    if (!rule.fromDate || !rule.toDate) return true;
    if (today < rule.fromDate) return true;
    if (today > rule.toDate) return false;
  }
  const target = parseMinutes(hhmm);
  if (target == null) return true;
  void nowTick;
  return target - currentIstMinutes() >= 2;
};

const emptySlot = () => ({ startTime: "", endTime: "" });

const ConsultantOnlineSchedule = ({ consultantId: consultantIdProp }) => {
  const { id } = useParams();
  const consultantId = consultantIdProp || id;
  const minDate = todayDateKey();
  const [nowTick, setNowTick] = useState(Date.now());
  const [mode, setMode] = useState("everyday");
  const [fromDate, setFromDate] = useState(minDate);
  const [toDate, setToDate] = useState(minDate);
  const [slots, setSlots] = useState([emptySlot()]);
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState({
    enabled: false,
    hasRules: false,
    rules: [],
    todayLabel: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);

  const loadSchedule = useCallback(async () => {
    if (!consultantId) return;
    try {
      setLoading(true);
      const { data } = await getConsultantOnlineScheduleApi({ consultantId });
      const result = data?.result || {};
      setSaved({
        enabled: Boolean(result.enabled),
        hasRules: Boolean(result.hasRules || result.rules?.length),
        rules: Array.isArray(result.rules) ? result.rules : [],
        todayLabel: result.todayLabel || result.label || "",
      });
    } catch (error) {
      toastMessage(error?.message || "Failed to load online hours", "error", "OnlineSchedule");
    } finally {
      setLoading(false);
    }
  }, [consultantId]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const persistRules = async (nextRules, clear = false) => {
    if (!consultantId) return false;
    try {
      setSaving(true);
      const { data } = await saveConsultantOnlineScheduleApi({
        consultantId,
        clear,
        enabled: !clear && nextRules.length > 0,
        rules: clear ? [] : nextRules,
      });
      if (data?.code === 200) {
        toastMessage(data.message, "success", "OnlineSchedule");
        await loadSchedule();
        return true;
      }
      toastMessage(data?.message || "Failed to save online hours", "error", "OnlineSchedule");
      return false;
    } catch (error) {
      toastMessage(error?.message || "Failed to save online hours", "error", "OnlineSchedule");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setMode("everyday");
    setFromDate(minDate);
    setToDate(minDate);
    setSlots([emptySlot()]);
  };

  const updateSlot = (index, key, value) => {
    setSlots((current) =>
      current.map((slot, slotIndex) => {
        if (slotIndex !== index) return slot;
        if (key === "startTime" && slot.canEditStart === false) return slot;
        if (key === "endTime" && slot.canEditEnd === false) return slot;
        const next = { ...slot, [key]: value };
        if (key === "startTime" && next.endTime && next.endTime <= value) {
          next.endTime = "";
        }
        return next;
      })
    );
  };

  const addSlotRow = () => {
    setSlots((current) => [...current, emptySlot()]);
  };

  const removeSlotRow = (index) => {
    setSlots((current) => (current.length === 1 ? [emptySlot()] : current.filter((_, slotIndex) => slotIndex !== index)));
  };

  const handleFromDate = (value) => {
    if (value && value < minDate) return;
    setFromDate(value);
    if (toDate && value && toDate < value) setToDate(value);
  };

  const handleToDate = (value) => {
    const minTo = fromDate || minDate;
    if (value && value < minTo) return;
    setToDate(value);
  };

  const startEditSlot = (rule, slot) => {
    const startOpen = canEditClockTime(rule, slot.startTime, nowTick);
    const endOpen = canEditClockTime(rule, slot.endTime, nowTick);
    if (!startOpen && !endOpen) {
      toastMessage("This time can be edited only until 2 minutes before it.", "error", "OnlineSchedule");
      return;
    }
    setEditing({ ruleId: rule.id, slotId: slot.id });
    setMode(rule.type === "range" ? "range" : "everyday");
    setFromDate(rule.fromDate || minDate);
    setToDate(rule.toDate || rule.fromDate || minDate);
    setSlots([{
      id: slot.id,
      startTime: slot.startTime,
      endTime: slot.endTime,
      canEditStart: startOpen,
      canEditEnd: endOpen,
    }]);
  };

  const addRule = async () => {
    const filledSlots = slots.filter((slot) => slot.startTime && slot.endTime);
    if (!filledSlots.length) {
      toastMessage("Add at least one start and end time", "error", "OnlineSchedule");
      return;
    }
    if (filledSlots.some((slot) => slot.endTime <= slot.startTime)) {
      toastMessage("End time must be after start time", "error", "OnlineSchedule");
      return;
    }
    if (mode === "range") {
      if (!fromDate || !toDate) {
        toastMessage("Select both start and end date", "error", "OnlineSchedule");
        return;
      }
      if (fromDate < minDate || toDate < minDate) {
        toastMessage("Previous dates cannot be booked", "error", "OnlineSchedule");
        return;
      }
      if (toDate < fromDate) {
        toastMessage("End date cannot be before start date", "error", "OnlineSchedule");
        return;
      }
    }

    if (editing?.ruleId) {
      const currentRule = (saved.rules || []).find((rule) => rule.id === editing.ruleId);
      const datesLocked = currentRule && !currentRule.canEditDates;
      const nextRules = (saved.rules || []).map((rule) => {
        if (rule.id !== editing.ruleId) return rule;
        const nextSlots = rule.slots.map((slot) => {
          if (editing.slotId && slot.id === editing.slotId) {
            return { ...slot, ...filledSlots[0], id: slot.id };
          }
          return slot;
        });
        const extras = filledSlots.slice(1);
        return {
          ...rule,
          type: datesLocked ? rule.type : (mode === "range" ? "range" : "everyday"),
          fromDate: datesLocked ? rule.fromDate : (mode === "range" ? fromDate : ""),
          toDate: datesLocked ? rule.toDate : (mode === "range" ? toDate : ""),
          slots: [...nextSlots, ...extras.map((slot) => ({ startTime: slot.startTime, endTime: slot.endTime }))],
        };
      });
      const ok = await persistRules(nextRules);
      if (ok) resetForm();
      return;
    }

    const nextRule = {
      type: mode === "range" ? "range" : "everyday",
      fromDate: mode === "range" ? fromDate : "",
      toDate: mode === "range" ? toDate : "",
      slots: filledSlots,
    };
    const ok = await persistRules([...(saved.rules || []), nextRule]);
    if (ok) resetForm();
  };

  const removeSlot = async (rule, slot) => {
    if (!canEditClockTime(rule, slot.startTime, nowTick)) {
      toastMessage(`Cannot edit ${toAmPm(slot.startTime)}. A booked time can be changed only until 2 minutes before it.`, "error", "OnlineSchedule");
      return;
    }
    const nextRules = (saved.rules || [])
      .map((item) => {
        if (item.id !== rule.id) return item;
        return { ...item, slots: (item.slots || []).filter((entry) => entry.id !== slot.id) };
      })
      .filter((item) => (item.slots || []).length > 0);
    await persistRules(nextRules, nextRules.length === 0);
    if (editing?.slotId === slot.id) resetForm();
  };

  const removeRule = async (rule) => {
    if (rule.canRemove === false || (rule.slots || []).some((slot) => !canEditClockTime(rule, slot.startTime, nowTick))) {
      toastMessage("Cannot remove this slot within 2 minutes of its start time.", "error", "OnlineSchedule");
      return;
    }
    const nextRules = (saved.rules || []).filter((item) => item.id !== rule.id);
    await persistRules(nextRules, nextRules.length === 0);
    if (editing?.ruleId === rule.id) resetForm();
  };

  const clearAll = async () => {
    const locked = (saved.rules || []).some((rule) => (
      rule.canRemove === false || (rule.slots || []).some((slot) => !canEditClockTime(rule, slot.startTime, nowTick))
    ));
    if (locked) {
      toastMessage("Cannot clear all. A booked time is within 2 minutes or has already started.", "error", "OnlineSchedule");
      return;
    }
    const ok = await persistRules([], true);
    if (ok) resetForm();
  };

  const todayNote = useMemo(() => {
    if (saved.todayLabel) return `Today: ${saved.todayLabel}`;
    if (saved.hasRules) return "No restriction today. Future or everyday rules are already saved.";
    return "No slot selected. This consultant can go online at any time.";
  }, [saved.hasRules, saved.todayLabel]);

  const datesLocked = Boolean(editing?.ruleId) && saved.rules.some((rule) => rule.id === editing.ruleId && rule.canEditDates === false);

  return (
    <div className="cons-ot-panel">
      <div className="cons-ot-header">
        <div>
          <h5>Scheduled Hours</h5>
          <div className="cons-ot-sub">
            Book everyday hours or a date range. Any booked time can be edited only until 2 minutes before it. Past dates are hidden.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="cons-ot-empty">Loading scheduled hours...</div>
      ) : (
        <>
          <div className="cons-slot-display">
            <span className="cons-slot-clock">⏱</span>
            <span className="cons-slot-time">
              {saved.todayLabel || "No slot today · Online anytime"}
            </span>
          </div>

          <div className={`cons-slot-note ${saved.enabled ? "" : "cons-slot-note-open"}`}>
            {todayNote}
          </div>

          {saved.rules?.length ? (
            <div className="cons-slot-rules">
              {saved.rules.map((rule) => (
                <div key={rule.id || rule.label} className="cons-slot-rule cons-slot-rule-stack">
                  <div className="cons-slot-rule-head">
                    <div className="cons-slot-rule-title">{rule.dateLabel || (rule.type === "everyday" ? "Everyday" : "Date range")}</div>
                    <button type="button" className="cons-ot-range" disabled={saving} onClick={() => removeRule(rule)}>
                      Remove
                    </button>
                  </div>
                  {(rule.slots || []).map((slot) => {
                    const startOpen = canEditClockTime(rule, slot.startTime, nowTick);
                    const endOpen = canEditClockTime(rule, slot.endTime, nowTick);
                    const lockedNote = !startOpen || !endOpen
                      ? `Locked times can be edited only until 2 minutes before ${!startOpen ? toAmPm(slot.startTime) : toAmPm(slot.endTime)}.`
                      : "";
                    return (
                      <div key={slot.id || slot.label} className="cons-slot-rule-line">
                        <div>
                          <div className="cons-slot-rule-slots">{slot.label || `${toAmPm(slot.startTime)} - ${toAmPm(slot.endTime)}`}</div>
                          {lockedNote ? <div className="cons-slot-lock-note">{lockedNote}</div> : null}
                        </div>
                        <div className="cons-slot-actions">
                          <button
                            type="button"
                            className={`cons-ot-range ${editing?.slotId === slot.id ? "active" : ""}`}
                            disabled={saving || (!startOpen && !endOpen)}
                            onClick={() => startEditSlot(rule, slot)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="cons-ot-range"
                            disabled={saving || !startOpen}
                            onClick={() => removeSlot(rule, slot)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : null}

          <div className="cons-slot-modes">
            <button
              type="button"
              className={`cons-ot-range ${mode === "everyday" ? "active" : ""}`}
              disabled={datesLocked}
              onClick={() => setMode("everyday")}
            >
              Everyday
            </button>
            <button
              type="button"
              className={`cons-ot-range ${mode === "range" ? "active" : ""}`}
              disabled={datesLocked}
              onClick={() => setMode("range")}
            >
              Date range
            </button>
          </div>

          {mode === "range" ? (
            <div className="cons-slot-form">
              <label>
                From date
                <input type="date" min={minDate} value={fromDate} disabled={datesLocked} onChange={(event) => handleFromDate(event.target.value)} />
              </label>
              <label>
                To date
                <input
                  type="date"
                  min={fromDate || minDate}
                  value={toDate}
                  disabled={datesLocked}
                  onChange={(event) => handleToDate(event.target.value)}
                />
              </label>
            </div>
          ) : (
            <div className="cons-slot-note cons-slot-note-open">
              {editing ? "Edit this booked slot. Locked times stay disabled until 2 minutes before them." : "Everyday slot applies every day, only between the booked times."}
            </div>
          )}

          {slots.map((slot, index) => (
            <div key={`slot-row-${slot.id || index}`} className="cons-slot-form">
              <label>
                Start time
                <select
                  value={slot.startTime}
                  disabled={slot.canEditStart === false}
                  onChange={(event) => updateSlot(index, "startTime", event.target.value)}
                >
                  <option value="">Select start</option>
                  {TIME_OPTIONS.map((item) => (
                    <option key={`start-${index}-${item.value}`} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                End time
                <select
                  value={slot.endTime}
                  disabled={slot.canEditEnd === false}
                  onChange={(event) => updateSlot(index, "endTime", event.target.value)}
                >
                  <option value="">Select end</option>
                  {TIME_OPTIONS.map((item) => (
                    <option
                      key={`end-${index}-${item.value}`}
                      value={item.value}
                      disabled={Boolean(slot.startTime) && item.value <= slot.startTime}
                    >
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="cons-slot-actions">
                {slots.length > 1 && !slot.id ? (
                  <button type="button" className="cons-ot-range" onClick={() => removeSlotRow(index)}>
                    Remove slot
                  </button>
                ) : null}
                {index === slots.length - 1 ? (
                  <button type="button" className="cons-ot-range" onClick={addSlotRow}>
                    + Another slot
                  </button>
                ) : null}
              </div>
            </div>
          ))}

          <div className="cons-slot-actions cons-slot-actions-footer">
            <button type="button" className="cons-ot-range active" disabled={saving} onClick={addRule}>
              {saving ? "Saving..." : editing ? "Save changes" : "Add hours"}
            </button>
            {editing ? (
              <button type="button" className="cons-ot-range" disabled={saving} onClick={resetForm}>
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              className="cons-ot-range"
              disabled={saving || !saved.hasRules}
              onClick={clearAll}
            >
              Clear all
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ConsultantOnlineSchedule;
